import { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { ShieldCheck, Feather } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { errMsg } from "@/lib/api";
import { SquareLogo, FrameCorners, KnotDivider } from "@/components/ornaments";
import { useT } from "@/lib/labels";
import { useNoIndex } from "@/admin/AdminLayout";

export default function AdminLogin() {
  const { user, login, verify2fa, recover } = useAuth();
  const { settings } = useSite();
  const t = useT();
  const [step, setStep] = useState("choose");
  useNoIndex();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/contribute"} replace />;

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try { await fn(); } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  const submit = (e) => {
    e.preventDefault();
    run(async () => {
      if (step === "admin") {
        const d = await login(email, password, "admin");
        if (d.twofa_required) { setChallenge(d); setStep("otp"); }
      } else if (step === "otp") await verify2fa(challenge.challenge, code);
      else if (step === "recover") await recover(email, code, password);
    });
  };
  const go = (s) => { setStep(s); setError(""); setCode(""); };

  return (
    <div className="min-h-screen grid place-items-center px-4 py-10">
      <form onSubmit={submit} data-testid="admin-login-form" className="relative vellum gilt w-full max-w-md p-10 rise">
        <FrameCorners size="w-12 h-12" />
        <div className="flex flex-col items-center text-center">
          <SquareLogo src={settings.logo_url} size="md" testid="login-square-logo" />
          <p className="eyebrow mt-6">{t("login.eyebrow")}</p>
          <h1 className="font-display text-3xl mt-1">{t("login.title")}</h1>
        </div>
        <KnotDivider className="my-6" />
        {step === "choose" && (
          <div className="grid gap-4" data-testid="login-portal-choice">
            <button type="button" data-testid="portal-admin-button" onClick={() => go("admin")} className="vellum p-5 text-left lift theme-flame">
              <span className="flex items-center gap-3 font-heading font-bold text-[color:var(--accent)]"><ShieldCheck className="w-5 h-5" /> {t("login.admin_choice")}</span>
              <span className="block text-sm italic mt-1 text-[#4a3a2a]">For the Lorekeeper only.</span>
            </button>
            <Link to="/join?tab=signin" data-testid="portal-reader-button" className="vellum p-5 text-left lift theme-frost block">
              <span className="flex items-center gap-3 font-heading font-bold text-[color:var(--accent)]"><Feather className="w-5 h-5" /> {t("login.reader_choice")}</span>
              <span className="block text-sm italic mt-1 text-[#4a3a2a]">Readers who write and submit articles. New readers can sign up here too.</span>
            </Link>
          </div>
        )}
        {(step === "admin" || step === "recover") && <>
          <p className="font-heading font-bold mb-4">{step === "admin" ? "Admin Login" : "Recover with a backup code"}</p>
          <label className="admin-label" htmlFor="email">Admin email</label>
          <input id="email" data-testid="admin-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="admin-input mb-4" />
          {step === "recover" && <>
            <label className="admin-label" htmlFor="rcode">Recovery code</label>
            <input id="rcode" data-testid="recover-code-input" required value={code} onChange={(e) => setCode(e.target.value)} placeholder="XXXX-XXXX-XXXX" className="admin-input mb-4 font-mono" />
          </>}
          <label className="admin-label" htmlFor="password">{step === "recover" ? "New password (optional)" : "Password"}</label>
          <input id="password" data-testid="admin-login-password" type="password" required={step === "admin"} value={password} onChange={(e) => setPassword(e.target.value)} className="admin-input" />
        </>}
        {step === "otp" && <>
          <p data-testid="otp-step" className="text-sm mb-4">{challenge?.method === "totp" ? <>Open <b>Microsoft / Google Authenticator</b> and enter the 6-digit code for Lorekeepers of Elirios.</> : challenge?.sms_sent ? <>We texted a 6-digit code to <b>{challenge.phone_hint}</b>.</> : <>SMS could not be sent. Enter one of your <b>recovery codes</b> instead.</>}</p>
          <label className="admin-label" htmlFor="otp">6-digit code (or a recovery code)</label>
          <input id="otp" data-testid="otp-code-input" required autoFocus value={code} onChange={(e) => setCode(e.target.value)} className="admin-input font-mono tracking-widest" />
        </>}
        {error && <p data-testid="admin-login-error" className="mt-4 text-sm text-[#991b1b]">{error}</p>}
        {step !== "choose" && <button data-testid="admin-login-button" disabled={busy} className="btn-illum theme-flame w-full justify-center mt-6 disabled:opacity-60">{busy ? "Unsealing…" : step === "otp" ? "Verify" : step === "recover" ? "Recover & sign in" : "Sign in"}</button>}
        <div className="flex justify-between mt-5 text-sm italic text-[#5e4a35]">
          {step === "choose" ? <Link to="/" className="hover:text-[#991b1b]">← Return to the codex</Link> : <button type="button" onClick={() => go("choose")} className="hover:text-[#991b1b]">← Back</button>}
          {step === "admin" && <button type="button" data-testid="recover-link" onClick={() => go("recover")} className="hover:text-[#991b1b]">Lost password or phone?</button>}
        </div>
      </form>
    </div>
  );
}
